import {
    clearJob,
    getJob,
    getRun,
    MAX_PHOTO_SIZE,
    MIN_PHOTO_SIDE,
    PHOTO_COUNT,
    PHOTO_TYPES,
    resumeJob,
    setViewing,
    startJob,
    TryOnError,
    updateJob,
} from '@theme/virtual-try-on-jobs';
import {createTryOnPhoto, getStoredPhoto, storePhoto} from '@theme/virtual-try-on-photo';
import {deleteSavedPhoto, getSavedPhotos, savePhoto} from '@theme/virtual-try-on-saved-photos';
import {
    addPiecesToCart,
    formatMoney,
    getOutfitImageUrl,
    getSizeOptionIndex,
    getSizeVariants,
    loadLook,
} from '@theme/virtual-try-on-look';
import {getAccountMediaFor} from '@theme/account-media-api';
// Toast when a video is ready while the popup is closed (also loaded store-wide from theme.liquid).
import '@theme/virtual-try-on-notifier';

/**
 * Virtual Try-On popup.
 *
 * 1. The shopper uploads 3 photos of themselves, which are sent to the Y.Chroma Virtual Try-on API
 *    to generate a short video of them wearing the outfit (see assets/virtual-try-on-jobs.js).
 * 2. While the video is generated the popup shows its progress. The shopper can close it and keep
 *    browsing: assets/virtual-try-on-notifier.js shows a toast when the video is ready.
 * 3. Once the video is ready it replaces the outfit image, and the look's products are shown:
 *    the variants are resolved to their products' handles in one tokenless Storefront API request,
 *    then each `/products/{handle}.js` is loaded, and its size can be changed (keeping the look's
 *    colour) and added to the bag.
 *
 * The shopper can also try the look on as a still photo instead (see assets/virtual-try-on-photo.js).
 * It's created while the popup stays open, then kept on the device like the video. Once one is ready,
 * a Video / Photo switch moves between them.
 *
 * A signed-in customer's finished videos and photos are also saved to their account, and the account's
 * Media page then clears them from the device (assets/account-media-api.js). So when the device has none
 * for the outfit, the popup shows the ones saved to the account.
 *
 * The shopper's photos are kept on the device (assets/virtual-try-on-saved-photos.js) and filled in the next
 * time the popup opens. Removing one in the popup, or on the account's Media page, deletes it.
 *
 * Exposed as `window.virtualTryOn({ tryOnId, imageUrl })` for the gallery button. Opening a product
 * page with `?vto={outfitId}` (the toast's link) opens the popup for that outfit.
 */

/**
 * @typedef {{
 *   tryOnId: string,
 *   imageUrl?: string,
 *   photos: (File | null)[],
 *   previews: Map<File, string>,
 *   consent: boolean,
 *   shareConsent: boolean,
 *   mode: 'video' | 'photo',
 *   photoRun: AbortController | null,
 *   photoUrl: string | null,
 *   videoUrl: string | null,
 * }} TryOnSession
 *
 * `videoUrl` is the video saved to the account, when the device has no job for the outfit.
 */

/**
 * The 3 photos the shopper uploads, in the order they're sent. `crop` positions the example photo
 * as in the design (mobile, then desktop).
 */
const PHOTO_SLOTS = [
    {
        title: 'Face — straight on',
        hint: 'Look at the camera, neutral face.',
        example: 'vto-example-1.jpg',
        crop: 'tw:top-[-1.71%] tw:left-[-152.16%] tw:!w-[416.64%] tw:!h-[399.05%] tw:md:top-[-12.45%] tw:md:left-[-79.9%] tw:md:!w-[265.8%] tw:md:!h-[439.18%]',
    },
    {
        title: 'Face — slight turn',
        hint: 'Turn your head a little to one side.',
        example: 'vto-example-2.jpg',
        crop: 'tw:top-[6.16%] tw:left-[-67.64%] tw:!w-[241.35%] tw:!h-[229.5%] tw:md:top-[-6.82%] tw:md:left-[-27.18%] tw:md:!w-[160.52%] tw:md:!h-[265.23%]',
    },
    {
        title: 'Full body',
        hint: 'Head to toe, standing straight.',
        example: 'vto-example-1.jpg',
        crop: 'tw:top-[14.73%] tw:left-[6.85%] tw:!w-[86.29%] tw:!h-[82.95%] tw:md:top-0 tw:md:left-[19.6%] tw:md:!w-[61.67%] tw:md:!h-full',
    },
];

/**
 * The slots the shopper fills for each kind of try-on (indexes into PHOTO_SLOTS). A photo only
 * needs the face straight on and the full body.
 */
const MODE_SLOTS = {video: [0, 1, 2], photo: [0, 2]};

// Popup layout. The first step is wider, with its own column split (see renderView).
const PANEL_WIDTH = 'tw:md:w-[min(1100px,calc(100%-32px))]';
const PANEL_WIDTH_WIDE = 'tw:md:w-[min(1300px,calc(100%-32px))]';
const BODY_COLUMNS = 'tw:md:grid-cols-2';
const BODY_COLUMNS_WIDE = 'tw:md:grid-cols-[520fr_780fr]';

/**
 * URL of a theme asset next to this script (the popup's icons and example photos).
 * @param {string} name
 */
function assetUrl(name) {
    return new URL(name, import.meta.url).href;
}


/** @type {HTMLElement | null} */
let modal = null;
/** @type {HTMLElement | null} */
let lastFocused = null;
/** @type {TryOnSession | null} */
let current = null;

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 */
function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
}


/**
 * Shows the outfit's image, or, when there's none, leaves the image out so its frame's plain
 * background shows as a placeholder instead of a broken image.
 * @param {HTMLImageElement} image
 * @param {string | undefined} url
 */
function setOutfitImage(image, url) {
    image.dataset.vtoOutfit = '';
    if (url) {
        image.src = url;
        image.style.visibility = '';
    } else image.style.visibility = 'hidden';
}

/**
 * Swaps the outfit image in the popup's current view, for when the look's image arrives after
 * the view was shown.
 * @param {string} url
 */
function updateOutfitImages(url) {
    modal?.querySelectorAll('img[data-vto-outfit]').forEach((image) => {
        if (/** @type {HTMLImageElement} */ (image).getAttribute('src') !== url) {
            setOutfitImage(/** @type {HTMLImageElement} */ (image), url);
        }
    });
}

function ensureModal() {
    if (modal) return modal;

    modal = el('div', 'tw:fixed tw:inset-0 tw:z-[1000] tw:flex tw:items-center tw:justify-center');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Virtual try on');
    modal.hidden = true;
    modal.innerHTML = `
    <div class="tw:absolute tw:inset-0 tw:bg-black/55" data-vto-close></div>
    <div class="vto-panel tw:relative tw:w-full tw:h-full tw:overflow-y-auto tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden tw:bg-white tw:outline-none tw:shadow-[0_30px_80px_rgba(0,0,0,0.35)] tw:md:h-auto tw:md:max-h-[calc(100vh-64px)] ${PANEL_WIDTH}" tabindex="-1">
      <button type="button" class="tw:absolute tw:top-0 tw:right-2 tw:z-[3] tw:flex tw:items-center tw:h-[35px] tw:px-[10px] tw:border-0 tw:bg-white/90 tw:cursor-pointer tw:md:h-12 tw:md:px-3 tw:md:bg-transparent tw:md:top-[14px] tw:md:right-[14px]" aria-label="Close" data-vto-close>
        <img class="tw:!w-[18px] tw:!h-[18px] tw:md:!w-8 tw:md:!h-8" src="${assetUrl('vto-icon-close.svg')}" width="32" height="32" alt="">
      </button>
      <div class="vto-body tw:relative tw:grid tw:grid-cols-1 tw:min-h-[300px] ${BODY_COLUMNS}"></div>
    </div>
  `;

    modal.addEventListener('click', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target.closest('[data-vto-size-guide-close]')) closeSizeGuide();
        else if (target.closest('[data-vto-close]')) closeModal();
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !modal || modal.hidden) return;
        // Escape closes the size guide or the consent first, then the popup.
        if (!closeSizeGuide() && !closeConsent()) closeModal();
    });

    document.body.appendChild(modal);
    return modal;
}

function openModal() {
    const root = ensureModal();
    lastFocused = /** @type {HTMLElement | null} */ (document.activeElement);
    root.hidden = false;
    document.documentElement.classList.add('tw:overflow-hidden');
    /** @type {HTMLElement} */ (root.querySelector('.vto-panel')).focus();
}

function closeModal() {
    if (!modal) return;
    // A try-on photo is only created while the popup is open, so stop polling for it.
    if (current) stopPhoto(current);
    closeSizeGuide();
    closeConsent();
    modal.hidden = true;
    document.documentElement.classList.remove('tw:overflow-hidden');
    setViewing(null);
    lastFocused?.focus();
}

/** The "Size guide" button that opened the guide, to return focus to it. */
let sizeGuideOpener = /** @type {HTMLElement | null} */ (null);

/**
 * Shows a size guide over the popup's right column (the whole popup on mobile), keeping the video
 * on the left.
 * @param {string} src
 * @param {string} productTitle
 */
function openSizeGuide(src, productTitle) {
    closeSizeGuide();
    sizeGuideOpener = /** @type {HTMLElement | null} */ (document.activeElement);

    const guide = el(
        'div',
        'vto-size-guide tw:fixed tw:inset-0 tw:z-[4] tw:overflow-y-auto tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden tw:overscroll-contain tw:bg-white tw:px-4 tw:pt-6 tw:pb-8 tw:md:absolute tw:md:left-[40%] tw:md:pt-[44px] tw:md:pb-12 tw:md:pl-14 tw:md:pr-16'
    );
    guide.setAttribute('role', 'dialog');
    guide.setAttribute('aria-label', `Size guide for ${productTitle}`);

    const close = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:absolute tw:top-1 tw:right-1 tw:flex tw:items-center tw:h-12 tw:px-3 tw:border-0 tw:bg-transparent tw:cursor-pointer tw:md:top-[14px] tw:md:right-[14px]'
        )
    );
    close.type = 'button';
    close.setAttribute('aria-label', 'Close size guide');
    close.dataset.vtoSizeGuideClose = '';
    close.append(icon('vto-icon-close.svg', 'tw:!w-8 tw:!h-8'));

    const back = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:self-start tw:p-0 tw:border-0 tw:bg-transparent tw:text-[13px] tw:font-medium tw:leading-normal tw:tracking-[1.04px] tw:text-black tw:underline tw:cursor-pointer',
            '← BACK TO THE LOOK'
        )
    );
    back.type = 'button';
    back.dataset.vtoSizeGuideClose = '';

    const title = el(
        'p',
        'tw:mt-4 tw:mb-0 tw:pr-12 tw:text-[24px] tw:font-normal tw:leading-normal tw:tracking-[1.2px] tw:uppercase tw:text-black tw:md:text-[28px]',
        `Size guide`
    );
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '2');

    const image = /** @type {HTMLImageElement} */ (el('img', 'tw:!w-[60%] tw:!h-auto tw:mt-6'));
    image.src = src;
    image.alt = `Size guide for ${productTitle}`;

    const content = el('div', 'tw:flex tw:flex-col');
    content.append(
        back,
        title,
        el('p', 'tw:mt-1 tw:mb-0 tw:text-[15px] tw:leading-normal tw:tracking-[0.6px] tw:uppercase tw:text-[#555]', productTitle),
        image
    );
    guide.append(close, content);
    /** @type {HTMLElement} */ (ensureModal().querySelector('.vto-body')).append(guide);
    close.focus();
}

function closeSizeGuide() {
    const guide = modal?.querySelector('.vto-size-guide');
    if (!guide) return false;
    guide.remove();
    sizeGuideOpener?.focus();
    sizeGuideOpener = null;
    return true;
}

/** The Photo & Video Consent, as [bold lead-in, text] paragraphs. */
const CONSENT_SECTIONS = [
    [
        '1. What you give us.',
        'Up to three photos of yourself (face straight on, face at a slight angle, full body) and the outfit you choose. To create the video, our technology analyzes your photos, which may involve measurements of your face and body that some laws treat as “biometric identifiers,” “biometric information” or “biometric data.”',
    ],
    [
        '2. What we use it for.',
        'Only to generate your personal AI try-on video and show it to you (and, if you sign in, save it in your account). We do not use your photos, video or biometric data for advertising profiles, to identify you, or to train or improve AI models.',
    ],
    [
        '3. Who processes it.',
        'Y.Chroma and a small number of service providers (such as our AI video and cloud-hosting partners) acting on our instructions under written contracts that require confidentiality, security, and deletion. We never sell, lease, trade, or otherwise profit from your photos or biometric data.',
    ],
    [
        '4. How long we keep it.',
        'Your original photos and any biometric data are permanently deleted within 30 days after your video is created, or sooner if you ask. If you have not signed in, your video is deleted after 30 days. If you have, it stays in your account until you delete it, and is deleted after 12 months of account inactivity.',
    ],
    [
        '5. AI-generated content.',
        'Your video is created by artificial intelligence and is labeled and watermarked as AI-generated. It is a styling preview: fit, color and drape of the real garments may differ.',
    ],
    [
        '6. Sharing and featuring.',
        'Your video is private unless you download or share it. We will feature it on our website or social media only if you tick the separate optional box, and you can withdraw that permission at any time.',
    ],
    [
        '7. Your rights.',
        'You can access, download, correct or delete your data, withdraw consent, and object to or restrict processing. Residents of California and other U.S. states may also request to know, delete, and limit use of sensitive personal information. EU/UK residents may complain to their data protection authority. Write to [privacy@ychroma.com].',
    ],
    [
        '8. International transfers.',
        'Your data may be processed in the United States and other countries. Where required, we use approved safeguards such as the EU Standard Contractual Clauses.',
    ],
    ['9. Your promise to us.', 'You confirm you are at least 18 years old and that the photos are of you.'],
    [
        '10. Your consent.',
        'By ticking the box, you give your express, informed, written consent for Y.Chroma and its service providers to collect, store and use your photos and biometric data as described above, under applicable law including the EU/UK GDPR, the Illinois Biometric Information Privacy Act (BIPA), the Texas CUBI Act, Washington’s biometric laws, and the CCPA/CPRA. Full details: [Privacy Policy] and [Biometric Data Retention Policy].',
    ],
];

/** The link that opened the consent, to return focus to it, and the popup's scroll before it opened. */
let consentOpener = /** @type {HTMLElement | null} */ (null);
let consentScrollTop = 0;

/**
 * Shows the Photo & Video Consent over the popup's right column (the whole popup on mobile),
 * keeping the look photo on the left. "I agree" calls `onAgree`.
 * @param {() => void} onAgree
 */
function openConsent(onAgree) {
    closeConsent();
    consentOpener = /** @type {HTMLElement | null} */ (document.activeElement);

    // The sheet fills the visible part of the popup, so its header and buttons stay in view.
    const panel = /** @type {HTMLElement} */ (ensureModal().querySelector('.vto-panel'));
    consentScrollTop = panel.scrollTop;
    panel.scrollTop = 0;
    panel.classList.add('tw:!overflow-hidden');

    const sheet = el(
        'div',
        'vto-consent tw:fixed tw:inset-0 tw:z-[4] tw:flex tw:flex-col tw:bg-white tw:md:absolute tw:md:bottom-auto tw:md:left-[40%]'
    );
    sheet.style.height = `${panel.clientHeight}px`;
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-label', 'Photo & Video Consent');

    /* Header: title and close */
    const header = el(
        'div',
        'tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:h-11 tw:pl-4 tw:pr-2 tw:border-0 tw:border-b tw:border-solid tw:border-[#e3e3e3] tw:md:h-auto tw:md:pt-6 tw:md:pb-4 tw:md:pl-14 tw:md:pr-10'
    );
    const title = el(
        'p',
        'tw:m-0 tw:text-[12px] tw:font-bold tw:leading-normal tw:tracking-[1.4px] tw:text-black tw:md:text-[22px] tw:md:font-normal tw:md:tracking-[1.3px]',
        'PHOTO & VIDEO CONSENT'
    );
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '2');
    const close = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:flex tw:items-center tw:gap-[6px] tw:px-[10px] tw:py-0 tw:border-0 tw:bg-transparent tw:text-[11px] tw:leading-normal tw:tracking-[1px] tw:text-black tw:cursor-pointer tw:md:gap-2 tw:md:px-0 tw:md:text-[13px]'
        )
    );
    close.type = 'button';
    close.append(el('span', '', 'CLOSE'), icon('vto-icon-close.svg', 'tw:!w-[18px] tw:!h-[18px] tw:md:!w-5 tw:md:!h-5'));
    close.addEventListener('click', closeConsent);
    header.append(title, close);

    /* The consent text */
    const text = el(
        'div',
        'tw:flex tw:flex-1 tw:flex-col tw:gap-3 tw:min-h-0 tw:overflow-y-auto tw:overscroll-contain tw:px-4 tw:pt-[18px] tw:pb-6 tw:text-[12px] tw:leading-[1.45] tw:text-black tw:md:pt-6 tw:md:pl-14 tw:md:pr-16 tw:md:text-[15px] tw:md:leading-[1.6]'
    );
    text.append(
        el(
            'p',
            'tw:m-0 tw:p-3 tw:bg-[#f4f3f1] tw:text-[12.5px] tw:md:px-5 tw:md:py-4 tw:md:text-[16px]',
            'In plain English: we use your photos only to make your video. We delete your photos and any face or body measurements within 30 days. We never sell them and never use them to train AI. You can delete your video, or change your mind, at any time.'
        )
    );
    CONSENT_SECTIONS.forEach(([lead, body]) => {
        const paragraph = el('p', 'tw:m-0');
        paragraph.append(el('strong', 'tw:font-bold', lead), ` ${body}`);
        text.append(paragraph);
    });
    text.append(
        el(
            'p',
            'tw:m-0 tw:text-[10.5px] tw:text-[#555] tw:md:text-[13px] tw:md:leading-normal',
            'Controller: Y.Chroma legal entity, 7636 Royal Lane, Dallas, TX 75230, United States. Last updated: 09/10/2026.'
        )
    );

    /* Footer: I agree / Not now */
    const footer = el(
        'div',
        'tw:flex tw:shrink-0 tw:gap-[10px] tw:px-4 tw:pt-3 tw:pb-4 tw:border-0 tw:border-t tw:border-solid tw:border-[#e3e3e3] tw:bg-white tw:md:gap-3 tw:md:pt-4 tw:md:pb-6 tw:md:pl-14 tw:md:pr-16'
    );
    const agree = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:flex-1 tw:h-11 tw:p-0 tw:border-0 tw:bg-black tw:text-[13px] tw:font-medium tw:leading-normal tw:tracking-[0.54px] tw:text-white tw:cursor-pointer tw:md:flex-none tw:md:w-[300px] tw:md:h-[62px] tw:md:text-[16px] tw:md:font-normal tw:md:tracking-[1px]',
            'I AGREE'
        )
    );
    agree.type = 'button';
    agree.addEventListener('click', () => {
        onAgree();
        closeConsent();
    });
    const notNow = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:flex-1 tw:h-11 tw:p-0 tw:border tw:border-solid tw:border-black tw:bg-white tw:text-[13px] tw:font-medium tw:leading-normal tw:tracking-[0.54px] tw:text-black tw:cursor-pointer tw:md:flex-none tw:md:w-[200px] tw:md:h-[62px] tw:md:border-[1.5px] tw:md:text-[16px] tw:md:font-normal tw:md:tracking-[1px]',
            'NOT NOW'
        )
    );
    notNow.type = 'button';
    notNow.addEventListener('click', closeConsent);
    footer.append(agree, notNow);

    sheet.append(header, text, footer);
    /** @type {HTMLElement} */ (ensureModal().querySelector('.vto-body')).append(sheet);
    close.focus();
}

function closeConsent() {
    const sheet = modal?.querySelector('.vto-consent');
    if (!sheet) return false;
    sheet.remove();
    const panel = /** @type {HTMLElement} */ (modal?.querySelector('.vto-panel'));
    panel.classList.remove('tw:!overflow-hidden');
    panel.scrollTop = consentScrollTop;
    consentOpener?.focus();
    consentOpener = null;
    return true;
}

/** @param {string} message */
function renderMessage(message) {
    setWide(false);
    const body = /** @type {HTMLElement} */ (ensureModal().querySelector('.vto-body'));
    body.replaceChildren(el('p', 'vto-message', message));
}

/**
 * Switches the popup between the wide first-step layout and the regular one.
 * @param {boolean} wide
 */
function setWide(wide) {
    const root = ensureModal();
    const panel = /** @type {HTMLElement} */ (root.querySelector('.vto-panel'));
    const body = /** @type {HTMLElement} */ (root.querySelector('.vto-body'));
    panel.classList.toggle(PANEL_WIDTH, !wide);
    panel.classList.toggle(PANEL_WIDTH_WIDE, wide);
    body.classList.toggle(BODY_COLUMNS, !wide);
    body.classList.toggle(BODY_COLUMNS_WIDE, wide);
}

/**
 * @param {HTMLElement} status
 * @param {string} message
 * @param {boolean} [isError]
 */
function setStatus(status, message, isError = false) {
    status.textContent = message;
    status.classList.toggle('is-error', isError);
}

/** Classes the size list toggles to slide open downward, as on the outfit-of-the-day page. */
const SIZE_MENU_CLOSED = ['tw:max-h-0', 'tw:opacity-0', 'tw:overflow-hidden'];
const SIZE_MENU_OPEN = ['tw:max-h-[60vh]', 'tw:opacity-100', 'tw:overflow-y-auto'];

/**
 * A "Shop this look" row: include checkbox, image, details and a size dropdown.
 * @param {any} item - { product, variant, sizeGuide: Promise<string | null> }
 * @param {() => void} onChange - called when the size or the checkbox changes
 * @param {() => void} closeOtherMenus - closes the other rows' size lists
 */
function renderItem(item, onChange, closeOtherMenus) {
    const {product, variant: lookVariant} = item;
    const sizeVariants = getSizeVariants(product, lookVariant);
    const sizeIndex = getSizeOptionIndex(product);
    const hasSizes = product.variants.length > 1;

    // The shopper picks every size ("Select size" until then).
    let selected = hasSizes ? null : lookVariant;
    let included = true;

    // Mobile: a grid so the size menu sits under the name, next to the image. Desktop: one line.
    const row = el(
        'li',
        'tw:grid tw:grid-cols-[22px_52px_minmax(0,1fr)] tw:items-start tw:gap-x-3 tw:gap-y-2 tw:py-[14px] tw:border-0 tw:border-b tw:border-solid tw:border-[#e3e3e3] tw:md:flex tw:md:gap-4'
    );

    /* Include in "Add the complete look" */
    const check = el('label', 'tw:relative tw:row-span-2 tw:shrink-0 tw:cursor-pointer');
    const checkbox = /** @type {HTMLInputElement} */ (el('input', 'tw:peer tw:sr-only'));
    checkbox.type = 'checkbox';
    checkbox.checked = true;
    checkbox.setAttribute('aria-label', `Include ${product.title}`);
    checkbox.addEventListener('change', () => {
        included = checkbox.checked;
        update();
        onChange();
    });
    check.append(
        checkbox,
        el(
            'span',
            "tw:relative tw:block tw:size-[22px] tw:border-[1.5px] tw:border-solid tw:border-black tw:rounded-[3px] tw:bg-white tw:peer-checked:bg-black tw:peer-focus-visible:outline-2 tw:peer-focus-visible:outline-offset-2 tw:peer-focus-visible:outline-[#dd4124] tw:after:absolute tw:after:hidden tw:after:top-[2px] tw:after:left-[6px] tw:after:w-[6px] tw:after:h-[11px] tw:after:border-white tw:after:border-r-2 tw:after:border-b-2 tw:after:rotate-45 tw:after:content-[''] tw:peer-checked:after:block tw:md:size-6 tw:md:border tw:md:rounded-none tw:md:after:top-[3px] tw:md:after:left-[7px] tw:md:after:w-[7px] tw:md:after:h-[12px]"
        )
    );

    const link = /** @type {HTMLAnchorElement} */ (
        el('a', 'tw:block tw:row-span-2 tw:shrink-0 tw:w-[52px] tw:h-[66px] tw:overflow-hidden tw:bg-[#f2f2f2] tw:md:w-16 tw:md:h-[82px]')
    );
    const image = /** @type {HTMLImageElement} */ (el('img', 'tw:!w-full tw:!h-full tw:object-cover'));
    image.alt = product.title;
    image.loading = 'lazy';
    link.append(image);

    /* Name, colour · size, price */
    const info = el('div', 'tw:flex tw:flex-1 tw:flex-col tw:gap-[2px] tw:min-w-0 tw:md:gap-1 tw:md:tracking-[0.6px]');
    const colour = lookVariant.options
        .filter((/** @type {string} */ _, /** @type {number} */ i) => i !== sizeIndex)
        .join(' / ');
    const summary = el('p', 'tw:hidden tw:m-0 tw:text-[14px] tw:leading-normal tw:text-[#545454] tw:md:block');
    const price = el('p', 'tw:hidden tw:m-0 tw:text-[14px] tw:leading-normal tw:text-[#545454] tw:md:block');
    // Mobile: "Colour · price" on one line (the size is in the menu below).
    const mobileSummary = el('p', 'tw:m-0 tw:text-[10.4px] tw:leading-normal tw:text-[#545454] tw:md:hidden');
    info.append(
        el(
            'p',
            'tw:m-0 tw:text-[10.4px] tw:font-bold tw:leading-normal tw:tracking-[0.42px] tw:uppercase tw:text-black tw:md:text-[15px] tw:md:tracking-[0.6px]',
            product.title
        ),
        summary,
        price,
        mobileSummary
    );

    /* Size dropdown: opens downward under its toggle */
    const sizeMenu = el('div', 'tw:col-start-3 tw:flex tw:flex-col tw:md:w-[211px] tw:md:shrink-0');
    const toggle = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:flex tw:items-center tw:justify-between tw:w-full tw:py-[9px] tw:px-0 tw:border-0 tw:border-y tw:border-solid tw:bg-transparent tw:text-left tw:cursor-pointer tw:disabled:cursor-default tw:md:py-[13px] tw:md:border-black'
        )
    );
    toggle.type = 'button';
    toggle.setAttribute('aria-expanded', 'false');
    const toggleLabel = el(
        'span',
        'tw:text-[10px] tw:font-medium tw:leading-normal tw:tracking-[1.6px] tw:uppercase tw:md:text-[12px] tw:md:tracking-[0.24px]'
    );
    const arrow = el('span', 'tw:inline-flex tw:transition-transform tw:duration-[350ms]');
    arrow.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true"><path d="M10.3535 0.353516L5.35352 5.35352L0.353516 0.353516" stroke="currentColor"/></svg>';
    toggle.append(toggleLabel, arrow);

    const options = el(
        'div',
        `tw:flex tw:flex-col tw:transition-[max-height,opacity] tw:duration-[350ms] tw:ease-in-out ${SIZE_MENU_CLOSED.join(' ')}`
    );
    options.setAttribute('role', 'listbox');
    options.setAttribute('aria-label', `Sizes for ${product.title}`);

    /** @param {boolean} open */
    function setOpen(open) {
        SIZE_MENU_CLOSED.forEach((name) => options.classList.toggle(name, !open));
        SIZE_MENU_OPEN.forEach((name) => options.classList.toggle(name, open));
        arrow.classList.toggle('tw:rotate-180', open);
        toggle.setAttribute('aria-expanded', String(open));
    }

    toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        if (open) closeOtherMenus();
        setOpen(open);
    });

    sizeVariants.forEach((/** @type {any} */ variant) => {
        const option = /** @type {HTMLButtonElement} */ (
            el(
                'button',
                'tw:block tw:w-full tw:py-1 tw:px-0 tw:border-0 tw:bg-transparent tw:text-left tw:text-[12px] tw:leading-normal tw:uppercase tw:text-black tw:cursor-pointer tw:first:pt-2 tw:disabled:text-gray-400 tw:disabled:line-through tw:disabled:cursor-not-allowed tw:aria-selected:font-bold',
                variant.options[sizeIndex]
            )
        );
        option.type = 'button';
        option.setAttribute('role', 'option');
        option.dataset.variantId = String(variant.id);
        option.disabled = !variant.available;
        option.addEventListener('click', () => {
            selected = variant;
            // Picking a size for a piece that was left out adds it back.
            included = checkbox.checked = true;
            setOpen(false);
            update();
            onChange();
        });
        options.append(option);
    });

    sizeMenu.append(toggle, options);

    // The size guide loads in the background: add its link under the dropdown once it's known.
    item.sizeGuide.then((/** @type {string | null} */ src) => {
        if (!src) return;
        const guide = /** @type {HTMLButtonElement} */ (
            el(
                'button',
                'tw:self-start tw:mt-2 tw:p-0 tw:border-0 tw:bg-transparent tw:text-[9px] tw:font-medium tw:leading-normal tw:tracking-[1.6px] tw:uppercase tw:text-black tw:underline tw:cursor-pointer tw:md:self-end tw:md:mt-4 tw:md:text-[10px] tw:md:tracking-[0.2px]',
                'Size guide'
            )
        );
        guide.type = 'button';
        guide.addEventListener('click', () => openSizeGuide(src, product.title));
        sizeMenu.append(guide);
    });

    function update() {
        const variant = selected || lookVariant;
        const featured = variant.featured_image?.src || product.featured_image;
        image.src = featured ? `${featured}${featured.includes('?') ? '&' : '?'}width=200` : '';
        link.href = `${product.url}?variant=${variant.id}`;
        price.textContent = selected && !selected.available ? `${formatMoney(variant.price)} · Sold out` : formatMoney(variant.price);
        mobileSummary.textContent = [colour, price.textContent].filter(Boolean).join(' · ');

        const size = selected && hasSizes ? selected.options[sizeIndex] : '';
        summary.replaceChildren(
            [colour && `Color: ${colour}`, hasSizes && 'Size: '].filter(Boolean).join(' · ')
        );
        if (hasSizes) summary.append(size || el('span', 'tw:text-[#545454]/50', 'Select size'));

        toggleLabel.textContent = size ? `Size: ${size}` : 'Select size';
        toggleLabel.classList.toggle('tw:text-[#dd4124]', !size);
        toggleLabel.classList.toggle('tw:text-black', !!size);
        // Mobile: the line and arrow are red until a size is picked. Desktop: the arrow shows once a
        // size is picked.
        toggle.classList.toggle('tw:border-[#dd4124]', !size);
        toggle.classList.toggle('tw:border-black', !!size);
        arrow.classList.toggle('tw:text-[#dd4124]', !size);
        arrow.classList.toggle('tw:text-black', !!size);
        arrow.classList.toggle('tw:md:invisible', !size);
        // A piece left out of the bag is greyed out.
        [link, info, sizeMenu].forEach((node) => node.classList.toggle('tw:opacity-40', !included));
        options.querySelectorAll('button').forEach((option) => {
            option.setAttribute('aria-selected', String(option.dataset.variantId === String(selected?.id)));
        });
    }

    row.append(check, link, info);
    if (hasSizes) row.append(sizeMenu);
    update();

    return {
        row,
        getSelected: () => selected,
        getPrice: () => (selected || lookVariant).price,
        isIncluded: () => included,
        closeMenu: () => setOpen(false),
        openMenu: () => setOpen(true),
        title: product.title,
        getLabel: () => (hasSizes && selected ? `${product.title} (${selected.options[sizeIndex]})` : product.title),
    };
}

/**
 * Builds the "Shop this look" list, and the "Add the complete look" button into `cta`.
 * @param {any} look
 * @param {any[]} items
 * @param {HTMLElement} status
 * @param {HTMLElement} cta
 */
function renderLook(look, items, status, cta) {
    const section = el('div', 'tw:flex tw:flex-col');
    const heading = el(
        'p',
        'tw:m-0 tw:text-[14px] tw:font-medium tw:leading-normal tw:tracking-[1.3px] tw:text-black tw:md:tracking-[1.4px]',
        'SHOP THIS LOOK'
    );
    heading.setAttribute('role', 'heading');
    heading.setAttribute('aria-level', '3');
    section.append(heading);

    if (!items.length) {
        section.append(el('p', 'tw:mt-[6px] tw:mb-0 tw:text-[15px] tw:leading-normal tw:text-[#444]', 'The products in this look are no longer available.'));
        return section;
    }

    const list = el('ul', 'tw:flex tw:flex-col tw:mt-1 tw:mb-0 tw:p-0 tw:list-none tw:md:mt-[6px]');
    const addAll = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:justify-center tw:w-full tw:min-h-[60px] tw:px-4 tw:py-3 tw:border-0 tw:bg-[#dd4124] tw:text-center tw:text-[15px] tw:leading-normal tw:tracking-[1px] tw:text-white tw:cursor-pointer tw:disabled:bg-[#c9c9c9] tw:disabled:cursor-not-allowed tw:md:min-h-[76px] tw:md:mt-[10px] tw:md:text-[17px] tw:md:tracking-[1.5px]'
        )
    );
    addAll.type = 'button';

    /** @param {string} label @param {number} [total] */
    function setAddAllLabel(label, total) {
        addAll.replaceChildren(el('span', 'tw:font-bold', label));
        if (total != null) addAll.append(el('span', 'tw:font-normal tw:whitespace-pre', ` — ${formatMoney(total)}`));
    }

    function updateAddAll() {
        const included = rows.filter((row) => row.isIncluded());
        // Every checked piece counts, at the look's price until its size is picked.
        const total = included.reduce((sum, row) => sum + row.getPrice(), 0);
        addAll.disabled = !included.length;
        // Every piece checked: it's the whole look. Otherwise just the checked pieces.
        const label = included.length === rows.length && rows.length > 1 ? 'ADD THE COMPLETE LOOK' : 'ADD TO BAG';
        setAddAllLabel(label, total ? total : undefined);
        setStatus(status, '');
    }

    const rows = items.map((item) =>
        renderItem(item, updateAddAll, () => rows.forEach((row) => row.closeMenu()))
    );
    rows.forEach(({row}) => list.append(row));
    section.append(list);

    const missing = (look.variantIds?.length || 0) - items.length;
    if (missing > 0) {
        section.append(
            el(
                'p',
                'tw:mt-3 tw:mb-0 tw:text-[13px] tw:leading-normal tw:text-[#454545]',
                `${missing} item${missing > 1 ? 's' : ''} from this look ${missing > 1 ? 'are' : 'is'} no longer available.`
            )
        );
    }

    section.append(
        el(
            'p',
            'tw:hidden tw:mt-5 tw:mb-0 tw:text-[13px] tw:leading-normal tw:text-[#454545] tw:md:block',
            'Pick a size for each piece. Free exchanges for a year.'
        )
    );

    addAll.addEventListener('click', async () => {
        // A checked piece without a size: open its list so the shopper can pick one.
        const needsSize = rows.find((row) => row.isIncluded() && !row.getSelected()?.available);
        if (needsSize) {
            rows.forEach((row) => row.closeMenu());
            needsSize.openMenu();
            setStatus(status, `Pick a size for ${needsSize.title}.`, true);
            return;
        }

        const pieces = rows
            .filter((row) => row.isIncluded() && row.getSelected()?.available)
            .map((row) => ({variant: row.getSelected(), label: row.getLabel()}));
        if (!pieces.length) return;

        addAll.disabled = true;
        setAddAllLabel('ADDING…');
        /** @type {string[]} */
        let problems = [];
        try {
            problems = await addPiecesToCart(pieces);
        } catch (error) {
            problems = [/** @type {Error} */ (error).message];
        }
        updateAddAll();
        if (problems.length) setStatus(status, problems.join('\n'), true);
    });

    updateAddAll();
    cta.append(
        addAll,
        el(
            'p',
            'tw:m-0 tw:text-center tw:text-[10px] tw:font-bold tw:leading-normal tw:tracking-[1.8px] tw:uppercase tw:text-[#454545] tw:md:hidden',
            'Free exchanges for a year.'
        )
    );
    return section;
}

/* ----- Popup views ----- */

/** @param {TryOnSession} session */
function isActive(session) {
    return current === session && !!modal && !modal.hidden;
}

/**
 * @param {HTMLElement} media
 * @param {HTMLElement} details
 * @param {boolean} [wide] - the first step's wider layout
 */
function renderView(media, details, wide = false) {
    setWide(wide);
    const body = /** @type {HTMLElement} */ (ensureModal().querySelector('.vto-body'));
    body.replaceChildren(media, details);
}

/** @param {string} [imageUrl] */
function outfitMedia(imageUrl) {
    const media = el('div', 'vto-media');
    const image = /** @type {HTMLImageElement} */ (el('img'));
    setOutfitImage(image, imageUrl);
    image.alt = 'Virtual try on outfit';
    media.append(image);
    return media;
}

/** @param {number} seconds */
function formatTime(seconds) {
    const total = Number.isFinite(seconds) ? Math.floor(seconds) : 0;
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * The generated video with its progress bar.
 * @param {string} videoUrl
 */
function videoMedia(videoUrl) {
    const media = el(
        'div',
        'tw:relative tw:overflow-hidden tw:bg-[#121212] tw:md:h-[min(900px,calc(100vh-64px))] tw:md:bg-[#111]'
    );

    // Mobile: the whole video at full width, its height following the video. Desktop: fitted into
    // the popup's height.
    const video = /** @type {HTMLVideoElement} */ (
        el(
            'video',
            'tw:relative tw:block tw:!w-full tw:!h-auto tw:cursor-pointer tw:md:absolute tw:md:inset-0 tw:md:!h-full tw:md:object-contain'
        )
    );
    ['autoplay', 'muted', 'loop', 'playsinline'].forEach((attribute) => video.setAttribute(attribute, ''));
    video.muted = true;
    video.src = videoUrl;

    const time = el('span', 'tw:shrink-0', '0:00');
    const duration = el('span', 'tw:shrink-0', '0:00');
    const track = el('div', 'tw:relative tw:flex-1 tw:h-[3px] tw:bg-white/35 tw:cursor-pointer');
    const fill = el('div', 'tw:absolute tw:inset-y-0 tw:left-0 tw:w-0 tw:bg-white');
    track.append(fill);
    track.setAttribute('aria-hidden', 'true');
    const controls = el(
        'div',
        'tw:absolute tw:inset-x-0 tw:bottom-[6px] tw:flex tw:items-center tw:gap-[14px] tw:h-8 tw:px-3 tw:text-[11px] tw:leading-normal tw:text-white tw:md:bottom-0 tw:md:h-10 tw:md:px-5 tw:md:text-[13px]'
    );
    // Desktop only: mobile has just the "Download video" button under the video.
    const download = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:hidden tw:shrink-0 tw:items-center tw:justify-center tw:size-8 tw:-mr-2 tw:p-0 tw:border-0 tw:bg-transparent tw:cursor-pointer tw:disabled:opacity-50 tw:disabled:cursor-wait tw:md:flex'
        )
    );
    download.type = 'button';
    download.setAttribute('aria-label', 'Download video');
    download.append(icon('vto-icon-download.svg', 'tw:!w-[18px] tw:!h-[18px] tw:invert'));
    download.addEventListener('click', () => downloadFile(videoUrl, download, 'mp4'));
    controls.append(time, track, duration, download);

    // Clicking the video plays or pauses it.
    video.addEventListener('click', () => (video.paused ? video.play().catch(() => {
    }) : video.pause()));
    video.addEventListener('loadedmetadata', () => (duration.textContent = formatTime(video.duration)));
    video.addEventListener('timeupdate', () => {
        time.textContent = formatTime(video.currentTime);
        fill.style.width = video.duration ? `${(video.currentTime / video.duration) * 100}%` : '0';
    });
    track.addEventListener('click', (event) => {
        if (!video.duration) return;
        const {left, width} = track.getBoundingClientRect();
        video.currentTime = ((event.clientX - left) / width) * video.duration;
    });

    media.append(
        video,
        el(
            'div',
            'tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-[110px] tw:bg-linear-to-b tw:from-transparent tw:to-black/70 tw:pointer-events-none tw:md:h-40'
        ),
        controls
    );
    return media;
}

/** File extensions for the downloaded video or photo, by MIME type. */
const DOWNLOAD_EXTENSIONS = /** @type {Record<string, string>} */ ({
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
});

/**
 * Saves the video or photo. A cross-origin link can't be downloaded directly, so it's fetched as a
 * blob first; if that fails it opens in a new tab instead.
 * @param {string} url
 * @param {HTMLButtonElement} button
 * @param {string} fallbackExtension - used when the file's type isn't known
 */
async function downloadFile(url, button, fallbackExtension) {
    button.disabled = true;
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const href = URL.createObjectURL(blob);
        const link = /** @type {HTMLAnchorElement} */ (el('a'));
        link.href = href;
        link.download = `ychroma-try-on.${DOWNLOAD_EXTENSIONS[blob.type.split(';')[0] ?? ''] || fallbackExtension}`;
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch {
        window.open(url, '_blank', 'noopener');
    } finally {
        button.disabled = false;
    }
}

/** @param {string} text */
function linkButton(text) {
    const button = /** @type {HTMLButtonElement} */ (el('button', 'vto-link-button', text));
    button.type = 'button';
    return button;
}

/**
 * Technical details for an error (HTTP status, request id, raw API response), for the console.
 * @param {unknown} error
 */
function errorDetails(error) {
    if (!(error instanceof TryOnError)) return '';

    const {response, ...info} = error.details;
    const lines = Object.entries(info)
        .filter(([, value]) => value != null && value !== '')
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`);
    if (response) {
        let body = response;
        try {
            body = JSON.stringify(JSON.parse(response), null, 2);
        } catch {
            // Not JSON: show as is
        }
        lines.push(`response:\n${body}`);
    }
    return lines.join('\n');
}

/**
 * Shows the error's message to the shopper; its technical details only go to the console.
 * @param {HTMLElement} status
 * @param {unknown} error
 */
function showError(status, error) {
    console.error('[virtual-try-on]', error, errorDetails(error));
    setStatus(status, /** @type {Error} */ (error).message, true);
}

/**
 * @param {string} name - asset file name
 * @param {string} className - size classes (`!` beats base.css's `img { width: 100%; height: auto }`)
 */
function icon(name, className) {
    const image = /** @type {HTMLImageElement} */ (el('img', `tw:shrink-0 ${className}`));
    image.src = assetUrl(name);
    image.alt = '';
    return image;
}

/**
 * Numbered step heading ("1 YOUR 3 PHOTOS").
 * @param {number} number
 * @param {string} title
 */
function stepHeader(number, title) {
    const header = el('div', 'tw:flex tw:items-center tw:gap-[10px] tw:md:gap-3');
    const heading = el(
        'p',
        'tw:m-0 tw:text-[16px] tw:font-medium tw:leading-normal tw:tracking-[1.28px] tw:text-black tw:md:text-[18px] tw:md:tracking-[1.44px]',
        title
    );
    heading.setAttribute('role', 'heading');
    heading.setAttribute('aria-level', '3');
    header.append(
        el(
            'span',
            'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:size-7 tw:rounded-full tw:bg-black tw:text-[14px] tw:leading-normal tw:text-white tw:md:size-8 tw:md:text-[15px]',
            String(number)
        ),
        heading
    );
    return header;
}

/** @param {string} [imageUrl] */
function setupMedia(imageUrl) {
    // Mobile shows the look in a banner at the top of the details instead (see mobileSetupHeader).
    const media = el('div', 'tw:relative tw:hidden tw:bg-[#f2f2f2] tw:md:block');
    const image = /** @type {HTMLImageElement} */ (
        el('img', 'tw:!w-full tw:aspect-[3/4] tw:object-cover tw:md:aspect-[520/977]')
    );
    setOutfitImage(image, imageUrl);
    image.alt = 'The look you picked';
    media.append(
        image,
        el(
            'p',
            'tw:absolute tw:top-6 tw:left-6 tw:m-0 tw:px-3 tw:py-2 tw:bg-[#f2f2f2] tw:text-[12px] tw:leading-normal tw:tracking-[1.2px] tw:text-black',
            'THE LOOK YOU PICKED'
        )
    );
    return media;
}

/** Mobile only: the "AI TRY-ON" bar at the top of the popup (the close button sits on its right). */
function mobileTopBar() {
    const bar = el(
        'p',
        'tw:flex tw:items-center tw:gap-[7px] tw:h-[35px] tw:m-0 tw:pl-[18px] tw:text-[12px] tw:font-bold tw:leading-normal tw:tracking-[1.4px] tw:text-[#dd4124] tw:md:hidden'
    );
    bar.append(icon('vto-icon-eyebrow.svg', 'tw:!w-[14px] tw:!h-[14px]'), el('span', '', 'AI TRY-ON'));
    return bar;
}

/**
 * Mobile only: the top bar and the look banner.
 * @param {string} [imageUrl]
 * @param {'video' | 'photo'} [mode]
 */
function mobileSetupHeader(imageUrl, mode = 'video') {
    const header = el('div', 'tw:-mx-4 tw:md:hidden');

    const banner = el('div', 'tw:flex tw:items-center tw:gap-[18px] tw:mt-[7px] tw:pr-[13px] tw:bg-[#f4f3f1]');
    const image = /** @type {HTMLImageElement} */ (el('img', 'tw:shrink-0 tw:!w-[52%] tw:aspect-[186/247] tw:object-contain'));
    setOutfitImage(image, imageUrl);
    image.alt = 'The look you picked';

    const text = el('div', 'tw:flex tw:flex-col tw:gap-[3px] tw:min-w-0 tw:text-black');
    const title = el('p', 'tw:m-0 tw:text-[17.4px] tw:font-medium tw:leading-[1.1] tw:tracking-[0.83px]', 'SEE YOURSELF IN THIS LOOK');
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '2');
    text.append(
        title,
        el(
            'p',
            'tw:m-0 tw:text-[12.5px] tw:leading-[1.33]',
            mode === 'photo'
                ? 'Two photos, and you’re wearing it.'
                : 'Three photos, under a minute, and you’re strolling down the streets of Lisbon in it.'
        ),
        el('p', 'tw:m-0 tw:text-[12.5px] tw:leading-[1.33]', '— Max')
    );

    banner.append(image, text);
    header.append(mobileTopBar(), banner);
    return header;
}

/**
 * Checks a photo can be decoded and is large enough for the video model.
 * @param {File} file
 * @returns {Promise<string | null>} Why the photo can't be used, or null if it can
 */
async function checkPhotoSize(file) {
    /** @type {ImageBitmap} */
    let bitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        return 'This file couldn’t be read as an image.';
    }
    const shortSide = Math.min(bitmap.width, bitmap.height);
    bitmap.close();
    if (shortSide < MIN_PHOTO_SIDE) {
        return `This photo is too small. Please use one that’s at least ${MIN_PHOTO_SIDE} x ${MIN_PHOTO_SIDE} pixels.`;
    }
    return null;
}

/**
 * Video / Photo switch, on the first step, the video's progress and the result. Switching shows
 * the other one if it's ready (see showMode), and keeps the photos already picked.
 * @param {TryOnSession} session
 * @param {string} className - its margins
 */
function modeSwitch(session, className) {
    const group = el(
        'div',
        `tw:grid tw:grid-cols-2 tw:border tw:border-solid tw:border-black tw:md:border-[1.2px] ${className}`
    );
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', 'Try it on as');

    /** @type {['video' | 'photo', string][]} */
    const modes = [
        ['video', 'VIDEO'],
        ['photo', 'PHOTO'],
    ];
    modes.forEach(([mode, label]) => {
        const option = /** @type {HTMLButtonElement} */ (
            el(
                'button',
                'tw:h-[41px] tw:p-0 tw:border-0 tw:bg-white tw:text-[12.5px] tw:font-medium tw:leading-normal tw:tracking-[0.54px] tw:text-black tw:cursor-pointer tw:aria-checked:bg-black tw:aria-checked:text-white tw:md:h-12 tw:md:text-[13px] tw:md:tracking-[0.9px]',
                label
            )
        );
        option.type = 'button';
        option.setAttribute('role', 'radio');
        option.setAttribute('aria-checked', String(session.mode === mode));
        option.addEventListener('click', () => {
            if (session.mode === mode) return;
            showMode(session, mode);
            /** @type {HTMLElement | null | undefined} */ (
                modal?.querySelector(`[role="radio"][aria-checked="true"]`)
            )?.focus();
        });
        group.append(option);
    });
    return group;
}

/**
 * Step 1: pick the photos (3 for a video, 2 for a photo), agree to the consent and send them.
 * @param {TryOnSession} session
 * @param {unknown} [error]
 */
function renderUpload(session, error) {
    const isPhoto = session.mode === 'photo';
    const activeSlots = MODE_SLOTS[session.mode];
    const slotCount = activeSlots.length;

    const details = el('div', 'tw:flex tw:flex-col tw:px-4 tw:md:pt-[44px] tw:md:pb-12 tw:md:pl-14 tw:md:pr-16');

    const eyebrow = el(
        'p',
        'tw:flex tw:items-center tw:gap-2 tw:m-0 tw:text-[13px] tw:font-bold tw:leading-normal tw:tracking-[1.8px] tw:text-[#dd4124]'
    );
    eyebrow.append(
        icon('vto-icon-eyebrow.svg', 'tw:!w-4 tw:!h-4'),
        el('span', '', isPhoto ? 'AI TRY-ON PHOTO' : 'AI TRY-ON VIDEO')
    );

    const title = el(
        'p',
        'tw:mt-[18px] tw:mb-0 tw:text-[24px] tw:font-normal tw:leading-normal tw:tracking-[1.4px] tw:text-black tw:md:text-[32px]',
        'SEE YOURSELF IN THIS LOOK'
    );
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '2');

    const intro = el(
        'div',
        'tw:flex tw:flex-col tw:gap-[10px] tw:mt-1 tw:pb-5 tw:text-[15px] tw:leading-[1.55] tw:text-black tw:md:pr-6 tw:md:text-[17px]'
    );
    const introText = el('p', 'tw:m-0');
    introText.append(
        el('strong', 'tw:font-bold', 'Short version: '),
        isPhoto
            ? 'two photos of you, and we show you in this outfit.'
            : 'we put you in this outfit and send you out for a stroll down the street in Lisbon. On video. In less than a minute.'
    );
    intro.append(introText, el('p', 'tw:m-0 tw:italic', '— Max'));

    const desktopHeader = el('div', 'tw:hidden tw:md:block');
    desktopHeader.append(eyebrow, title, intro);
    details.append(
        mobileSetupHeader(session.imageUrl, session.mode),
        desktopHeader,
        modeSwitch(session, 'tw:mt-[22px] tw:md:mt-[30px]')
    );

    // Came here from the result to make a new one: offer the way back to it.
    const kept = getResult(session, session.mode);
    if (kept) {
        const back = linkButton(isPhoto ? '← Back to your photo' : '← Back to your video');
        back.classList.add('tw:self-start');
        back.addEventListener('click', () => showResult(session, kept, session.mode));
        details.append(back);
    }

    // Skip the try-on and go straight to the look's products.
    const shopLook = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:self-start tw:mt-3 tw:p-0 tw:border-0 tw:bg-transparent tw:text-left tw:text-[13px] tw:leading-normal tw:text-black tw:cursor-pointer tw:md:mt-[14px] tw:md:text-[15px]'
        )
    );
    shopLook.type = 'button';
    shopLook.append('Not today? ', el('span', 'tw:underline', 'Just shop the look →'));
    shopLook.addEventListener('click', () => renderShopLook(session));
    details.append(shopLook);

    /* Step 1: the photos */
    const fileInput = /** @type {HTMLInputElement} */ (el('input'));
    fileInput.type = 'file';
    fileInput.accept = PHOTO_TYPES.join(',');
    fileInput.hidden = true;

    /** Slot the next picked file goes into; null fills the empty slots in order. */
    let targetSlot = /** @type {number | null} */ (null);
    /** Why the last photo picked for a slot wasn't accepted, shown under that slot. */
    const slotErrors = /** @type {Map<number, string>} */ (new Map());
    /** Picks are checked one after another, so each sees the slots the previous one filled. */
    let picking = Promise.resolve();

    /** @param {number | null} slot */
    function pickFiles(slot) {
        targetSlot = slot;
        fileInput.multiple = slot == null;
        fileInput.click();
    }

    const slots = el(
        'ul',
        `tw:grid ${isPhoto ? 'tw:grid-cols-2' : 'tw:grid-cols-3'} tw:gap-2 tw:mt-3 tw:mb-0 tw:py-0 tw:pr-0 tw:pl-[35px] tw:list-none tw:md:gap-[14px] tw:md:mt-[14px] tw:md:pl-0`
    );

    const uploadButton = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:flex tw:items-center tw:justify-center tw:gap-2 tw:h-[41px] tw:mt-[21px] tw:px-3 tw:border tw:border-solid tw:border-black tw:bg-white tw:text-[12.5px] tw:font-medium tw:leading-normal tw:tracking-[0.54px] tw:text-black tw:cursor-pointer tw:md:h-12 tw:md:mt-[31px] tw:md:border-[1.2px] tw:md:text-[12px] tw:md:tracking-[0.48px]'
        )
    );
    uploadButton.type = 'button';
    uploadButton.append(
        icon('vto-icon-upload.svg', 'tw:hidden tw:!w-4 tw:!h-4 tw:md:block'),
        el('span', 'tw:md:hidden', 'CHOOSE FROM MY PHOTOS'),
        el('span', 'tw:hidden tw:md:inline', 'UPLOAD FROM COMPUTER')
    );
    uploadButton.addEventListener('click', () => pickFiles(null));

    const tips = el(
        'p',
        'tw:mt-[10px] tw:mb-0 tw:text-[13px] tw:leading-[1.45] tw:text-[#444] tw:md:mt-[6px] tw:md:text-[15px] tw:md:leading-[1.4]',
        'Tips: good daylight, no hat or sunglasses, a plain wall behind you.'
    );
    tips.append(
        el(
            'span',
            'tw:block tw:mt-1 tw:text-[11px] tw:md:text-[13px]',
            'Your photos stay on this device for next time. Remove them here, or under Media in your account.'
        )
    );

    const photoStep = stepHeader(1, `YOUR ${slotCount} PHOTOS`);
    photoStep.classList.add('tw:mt-[22px]', 'tw:md:mt-[30px]');
    details.append(photoStep, fileInput, slots, uploadButton, tips);

    /* Step 2: consent and create */
    /**
     * @param {(string | Node)[]} text
     * @param {boolean} checked
     * @param {(checked: boolean) => void} onChange
     */
    function checkbox(text, checked, onChange) {
        const label = el(
            'label',
            'tw:flex tw:items-start tw:gap-3 tw:pl-[35px] tw:text-[11px] tw:leading-[1.4] tw:text-black tw:cursor-pointer tw:md:gap-[14px] tw:md:pl-0 tw:md:text-[14px] tw:md:leading-[1.5]'
        );
        const input = /** @type {HTMLInputElement} */ (el('input', 'tw:peer tw:sr-only'));
        input.type = 'checkbox';
        input.checked = checked;
        input.addEventListener('change', () => {
            onChange(input.checked);
            update();
        });
        const box = el(
            'span',
            "tw:relative tw:shrink-0 tw:size-[22px] tw:border-[1.5px] tw:border-solid tw:border-black tw:rounded-[3px] tw:bg-white tw:peer-checked:bg-black tw:peer-focus-visible:outline-2 tw:peer-focus-visible:outline-offset-2 tw:peer-focus-visible:outline-[#dd4124] tw:after:absolute tw:after:hidden tw:after:top-[2px] tw:after:left-[6px] tw:after:w-[6px] tw:after:h-[11px] tw:after:border-white tw:after:border-r-2 tw:after:border-b-2 tw:after:rotate-45 tw:after:content-[''] tw:peer-checked:after:block tw:md:size-5 tw:md:after:top-px tw:md:after:left-[5px] tw:md:after:w-[5px] tw:md:after:h-[10px]"
        );
        const content = el('span', 'tw:flex-1');
        content.append(...text);
        label.append(input, box, content);
        return label;
    }

    // Opens the full consent; it doesn't tick the box (a link inside a label shouldn't).
    const consentLink = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:inline tw:p-0 tw:border-0 tw:bg-transparent tw:font-[inherit] tw:text-[length:inherit] tw:leading-[inherit] tw:text-black tw:underline tw:cursor-pointer',
            'Photo & Video Consent'
        )
    );
    consentLink.type = 'button';
    consentLink.addEventListener('click', (event) => {
        event.preventDefault();
        openConsent(() => {
            session.consent = true;
            consentInput.checked = true;
            update();
        });
    });

    const consent = checkbox(
        [
            `I agree that Y.Chroma may use my photos to create my personal AI ${isPhoto ? 'photo' : 'video'}, as explained in the `,
            consentLink,
            '. (Required)',
        ],
        session.consent,
        (checked) => (session.consent = checked)
    );
    const consentInput = /** @type {HTMLInputElement} */ (consent.querySelector('input'));
    consent.classList.add('tw:mt-3', 'tw:md:mt-4');
    const shareConsent = checkbox(
        ['Y.Chroma may also feature my video on its website and social media. (Optional)'],
        session.shareConsent,
        (checked) => (session.shareConsent = checked)
    );
    shareConsent.classList.add('tw:mt-3', 'tw:md:mt-1');
    // Featuring is about the video: a photo isn't kept.
    shareConsent.hidden = isPhoto;

    const submit = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:flex tw:items-center tw:justify-center tw:gap-[10px] tw:w-full tw:h-[60px] tw:border-0 tw:bg-[#dd4124] tw:text-[17px] tw:font-bold tw:leading-normal tw:tracking-[1.3px] tw:text-white tw:cursor-pointer tw:disabled:bg-[#c9c9c9] tw:disabled:cursor-not-allowed tw:md:gap-3 tw:md:h-[72px] tw:md:text-[19px] tw:md:tracking-[1.5px]'
        )
    );
    submit.type = 'button';
    submit.append(
        icon('vto-icon-create.svg', 'tw:!w-[18px] tw:!h-[18px] tw:md:!w-[22px] tw:md:!h-[22px]'),
        el('span', '', isPhoto ? 'CREATE MY PHOTO' : 'CREATE MY VIDEO')
    );

    const hint = el(
        'p',
        'tw:m-0 tw:text-center tw:text-[10px] tw:font-bold tw:leading-normal tw:tracking-[1.8px] tw:uppercase tw:text-[#dd4124] tw:empty:hidden tw:md:mt-3 tw:md:text-[13px]'
    );

    const status = el(
        'p',
        'tw:m-0 tw:text-center tw:text-[13px] tw:text-black tw:empty:hidden tw:[&.is-error]:text-[#dd4124] tw:md:mt-3'
    );
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    // Mobile: the button stays at the bottom of the screen while the popup scrolls.
    const cta = el(
        'div',
        'tw:sticky tw:bottom-0 tw:z-[2] tw:flex tw:flex-col tw:gap-2 tw:-mx-4 tw:mt-6 tw:px-4 tw:pt-3 tw:pb-4 tw:bg-white tw:md:static tw:md:block tw:md:mx-0 tw:md:mt-[22px] tw:md:p-0'
    );
    cta.append(submit, hint, status);

    const createStep = stepHeader(2, isPhoto ? 'CREATE YOUR PHOTO' : 'CREATE YOUR VIDEO');
    createStep.classList.add('tw:mt-[34px]', 'tw:md:mt-[50px]');
    details.append(createStep, consent, shareConsent, cta);

    /** @param {number} index */
    function removePhoto(index) {
        const photo = session.photos[index];
        if (!photo) return;
        URL.revokeObjectURL(/** @type {string} */ (session.previews.get(photo)));
        session.previews.delete(photo);
        session.photos[index] = null;
    }

    function update() {
        slots.replaceChildren();
        activeSlots.forEach((index) => {
            const slot = PHOTO_SLOTS[index];
            if (!slot) return;
            const photo = session.photos[index];
            const item = el('li', 'tw:flex tw:flex-col tw:min-w-0');
            const box = el(
                'div',
                'tw:relative tw:overflow-hidden tw:aspect-[92.67/129] tw:border tw:border-solid tw:border-[#d6d6d6] tw:bg-white tw:md:aspect-[210.67/170] tw:md:border-2 tw:md:border-dashed tw:md:border-[#b9b9b9]'
            );

            if (photo) {
                let src = session.previews.get(photo);
                if (!src) {
                    src = URL.createObjectURL(photo);
                    session.previews.set(photo, src);
                }
                const image = /** @type {HTMLImageElement} */ (
                    el('img', 'tw:absolute tw:inset-0 tw:!w-full tw:!h-full tw:object-cover')
                );
                image.src = src;
                image.alt = slot.title;

                const remove = /** @type {HTMLButtonElement} */ (
                    el(
                        'button',
                        'tw:absolute tw:top-1 tw:right-1 tw:flex tw:items-center tw:justify-center tw:size-6 tw:p-0 tw:border-0 tw:rounded-full tw:bg-black/70 tw:text-[16px] tw:leading-none tw:text-white tw:cursor-pointer',
                        '×'
                    )
                );
                remove.type = 'button';
                remove.setAttribute('aria-label', `Remove photo: ${slot.title}`);
                remove.addEventListener('click', () => {
                    removePhoto(index);
                    deleteSavedPhoto(index);
                    slotErrors.delete(index);
                    setStatus(status, '');
                    update();
                });

                box.append(image, remove);
            } else {
                const add = /** @type {HTMLButtonElement} */ (
                    el('button', 'tw:absolute tw:inset-0 tw:overflow-hidden tw:p-0 tw:border-0 tw:bg-transparent tw:cursor-pointer')
                );
                add.type = 'button';
                add.setAttribute('aria-label', `Add photo: ${slot.title}`);
                const example = /** @type {HTMLImageElement} */ (el('img', `tw:absolute tw:!max-w-none ${slot.crop}`));
                example.src = assetUrl(slot.example);
                example.alt = '';
                add.append(
                    example,
                    el(
                        'span',
                        'tw:absolute tw:top-1 tw:left-1 tw:px-[5px] tw:py-[2px] tw:bg-white tw:text-[9px] tw:font-bold tw:leading-normal tw:tracking-[0.9px] tw:text-black tw:md:top-[5.5px] tw:md:left-[4.88px] tw:md:px-[5.25px] tw:md:py-[2.25px] tw:md:text-[8.25px] tw:md:tracking-[0.825px]',
                        'EXAMPLE'
                    )
                );
                add.addEventListener('click', () => pickFiles(index));
                box.append(add);
            }

            item.append(
                box,
                el(
                    'p',
                    'tw:m-0 tw:pt-[6px] tw:text-[11px] tw:font-bold tw:leading-[1.15] tw:tracking-[0.22px] tw:uppercase tw:text-black tw:md:pt-2 tw:md:text-[15px] tw:md:leading-normal tw:md:tracking-normal',
                    slot.title
                ),
                el('p', 'tw:hidden tw:m-0 tw:text-[13px] tw:leading-[1.35] tw:text-[#555] tw:md:block', slot.hint)
            );

            const slotError = slotErrors.get(index);
            if (slotError) {
                const message = el(
                    'p',
                    'tw:m-0 tw:pt-[6px] tw:text-[11px] tw:leading-[1.35] tw:text-[#dd4124] tw:md:text-[13px]',
                    slotError
                );
                message.id = `vto-photo-error-${index}`;
                box.querySelector('button')?.setAttribute('aria-describedby', message.id);
                item.append(message);
            }
            slots.append(item);
        });

        const missing = activeSlots.filter((index) => !session.photos[index]).length;
        submit.disabled = missing > 0 || !session.consent;
        if (missing === slotCount) hint.textContent = `Add your ${slotCount} photos to continue.`;
        else if (missing) hint.textContent = `Add ${missing} more photo${missing > 1 ? 's' : ''} to continue.`;
        else hint.textContent = session.consent ? '' : 'Agree to the photo consent to continue.';
    }

    /**
     * @param {File[]} files
     * @param {number | null} slot
     */
    async function addFiles(files, slot) {
        /** @type {string[]} */
        const errors = [];
        let tooMany = false;
        /** Slots this pick has used, so a rejected photo's slot isn't filled by the next file. */
        const used = new Set();
        for (const file of files) {
            if (!PHOTO_TYPES.includes(file.type)) errors.push(`${file.name} isn’t a JPEG, PNG or WebP image.`);
            else if (file.size > MAX_PHOTO_SIZE) errors.push(`${file.name} is larger than 10 MB.`);
            else {
                const index = slot ?? activeSlots.find((i) => !session.photos[i] && !used.has(i)) ?? -1;
                if (index === -1) {
                    tooMany = true;
                    continue;
                }
                used.add(index);
                slot = null;

                // A rejected photo leaves the slot as it was (empty, or the previous photo).
                const problem = await checkPhotoSize(file);
                if (problem) {
                    slotErrors.set(index, problem);
                    continue;
                }
                slotErrors.delete(index);
                removePhoto(index);
                session.photos[index] = file;
                savePhoto(index, file);
            }
        }
        if (tooMany) errors.push(`Only ${slotCount} photos can be used. Remove one to add another.`);

        setStatus(status, errors.join(' '), errors.length > 0);
        update();
    }

    fileInput.addEventListener('change', () => {
        const files = Array.from(fileInput.files || []);
        const slot = targetSlot;
        fileInput.value = '';
        targetSlot = null;
        picking = picking.then(() => addFiles(files, slot));
    });

    submit.addEventListener('click', () => {
        const photos = /** @type {File[]} */ (activeSlots.map((index) => session.photos[index]).filter(Boolean));
        if (photos.length !== slotCount || !session.consent) return;
        submit.disabled = true;

        if (isPhoto) {
            if (!session.photoRun) startPhoto(session, photos);
            return;
        }

        // A new video: its waiting screen starts from the beginning.
        videoWaitStarts.set(session.tryOnId, Date.now());
        showProgress(
            session,
            startJob(session.tryOnId, photos, {
                productUrl: location.pathname,
                imageUrl: session.imageUrl,
                shareConsent: session.shareConsent,
            })
        );
    });

    if (error) showError(status, error);
    update();

    renderView(setupMedia(session.imageUrl), details, true);
}

/**
 * The waiting screen's stages. The API doesn't report its progress, so the stages and the
 * percentage follow a set timeline: each stage lasts `seconds` and ends at `percent`. The video's
 * timeline then holds at 99% until it's ready; the photo's last stage is open-ended and creeps
 * towards 99%. A photo is quicker, and done as soon as it's generated, so it has no final stage.
 * @type {Record<'video' | 'photo', { label: string, percent: number, seconds: number }[]>}
 */
const WAIT_STAGES = {
    video: [
        // 26 seconds in all, then it holds at 99% until the video is ready.
        {label: 'Studying your photos', percent: 25, seconds: 2},
        {label: 'Tailoring the pieces to you', percent: 50, seconds: 7},
        {label: 'Taking you out for a walk', percent: 90, seconds: 13},
        {label: 'Final touches', percent: 99, seconds: 4},
    ],
    photo: [
        {label: 'Studying your photos', percent: 25, seconds: 5},
        {label: 'Tailoring the pieces to you', percent: 60, seconds: 15},
        {label: 'Taking you out for a walk', percent: 99, seconds: Infinity},
    ],
};

/**
 * Where the timeline is after `elapsed` seconds.
 * @param {number} elapsed
 * @param {'video' | 'photo'} kind
 * @returns {{ stage: number, percent: number }}
 */
function waitProgress(elapsed, kind) {
    const stages = WAIT_STAGES[kind];
    let from = 0;
    let time = Math.max(0, elapsed);
    for (let stage = 0; stage < stages.length; stage++) {
        const {percent, seconds} = stages[stage];
        if (time < seconds) {
            // Linear within a stage; the open-ended last stage eases out instead.
            const share = Number.isFinite(seconds) ? time / seconds : 1 - Math.exp(-time / 90);
            return {stage, percent: from + (percent - from) * share};
        }
        time -= seconds;
        from = percent;
    }
    return {stage: stages.length - 1, percent: from};
}

/** When each outfit's video started, so reopening the popup carries on the same timeline. */
const videoWaitStarts = /** @type {Map<string, number>} */ (new Map());

/** The waiting screen's text for a video or a photo. */
const WAIT_COPY = {
    video: {
        title: 'CREATING YOUR VIDEO',
        text: 'Good things take a moment — less than a minute, actually. Feel free to keep browsing: we’ll tell you right here the moment it’s ready.',
    },
    photo: {
        title: 'CREATING YOUR PHOTO',
        text: 'Good things take a moment. Stick around: if you close this window, your photo stops.',
    },
};

/**
 * Step 2: the waiting screen, with the outfit blurred behind the percentage, a progress bar and
 * the stages. It updates itself until it leaves the page.
 * @param {TryOnSession} session
 * @param {'video' | 'photo'} kind
 * @param {number} startedAt - when the timeline starts (ms)
 * @returns {{ details: HTMLElement, finish: () => Promise<void> }} `finish` fills it up to 100%
 */
function renderWaiting(session, kind, startedAt) {
    const copy = WAIT_COPY[kind];

    // The outfit, blurred under a white veil, with the percentage and the current stage.
    const media = el('div');
    const visual = el(
        'div',
        'tw:relative tw:h-[300px] tw:overflow-hidden tw:bg-[#e9e8e6] tw:md:h-[min(900px,calc(100vh-64px))]'
    );
    const image = /** @type {HTMLImageElement} */ (
        el(
            'img',
            'tw:absolute tw:inset-x-0 tw:top-[-60px] tw:!w-full tw:!h-[calc(100%+323px)] tw:object-cover tw:blur-[14px] tw:md:inset-0 tw:md:!h-full'
        )
    );
    setOutfitImage(image, session.imageUrl);
    image.alt = '';
    const percentText = el('p', 'tw:m-0 tw:text-[44px] tw:leading-normal tw:md:text-[64px]');
    const stageText = el('p', 'tw:m-0 tw:text-[11px] tw:leading-normal tw:tracking-[1.5px] tw:md:text-[14px] tw:md:tracking-[1.7px]');
    stageText.setAttribute('role', 'status');
    stageText.setAttribute('aria-live', 'polite');
    const readout = el(
        'div',
        'tw:absolute tw:inset-0 tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-[6px] tw:text-black tw:whitespace-nowrap tw:md:gap-3'
    );
    readout.append(percentText, stageText);
    visual.append(image, el('div', 'tw:absolute tw:inset-0 tw:bg-white/50'), readout);
    media.append(mobileTopBar(), visual);

    const details = el(
        'div',
        'tw:flex tw:flex-col tw:items-start tw:px-4 tw:pt-[22px] tw:pb-6 tw:md:pt-[44px] tw:md:pb-12 tw:md:pl-14 tw:md:pr-16 tw:md:h-[min(900px,calc(100vh-64px))] tw:md:overflow-y-auto tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden tw:md:overscroll-contain'
    );

    const title = el(
        'p',
        'tw:mt-[6px] tw:mb-0 tw:text-[20px] tw:font-medium tw:leading-normal tw:tracking-[1px] tw:text-black tw:md:mt-[10px] tw:md:text-[32px] tw:md:font-normal tw:md:tracking-[1.4px]',
        copy.title
    );
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '2');

    const bar = el('div', 'tw:relative tw:self-stretch tw:h-2 tw:mt-5 tw:overflow-hidden tw:bg-[#ededed] tw:md:h-[10px] tw:md:mt-[34px] tw:md:bg-[#eee]');
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-label', copy.title.toLowerCase());
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');
    const fill = el('div', 'tw:absolute tw:inset-y-0 tw:left-0 tw:bg-[#dd4124] tw:transition-[width] tw:duration-500 tw:ease-linear');
    bar.append(fill);

    const stages = el('ol', 'tw:flex tw:flex-col tw:gap-3 tw:m-0 tw:mt-5 tw:p-0 tw:list-none tw:md:gap-4 tw:md:mt-[26px]');
    const waitStages = WAIT_STAGES[kind];
    const stageItems = waitStages.map(({label}) => {
        const item = el('li', 'tw:flex tw:items-center tw:gap-3 tw:text-[14px] tw:leading-normal tw:md:gap-[14px] tw:md:text-[17px]');
        const dot = el('span', 'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:size-[22px] tw:rounded-full tw:border-2 tw:border-solid tw:md:size-7');
        item.append(dot, el('span', '', label));
        stages.append(item);
        return {item, dot};
    });

    details.append(
        el(
            'p',
            'tw:m-0 tw:text-[12px] tw:font-bold tw:leading-normal tw:tracking-[1.4px] tw:text-[#dd4124] tw:md:text-[13px] tw:md:tracking-[1.8px]',
            'HANG TIGHT'
        ),
        title,
        el(
            'p',
            'tw:mt-2 tw:mb-0 tw:text-[13px] tw:leading-[1.4] tw:text-[#333] tw:md:mt-[14px] tw:md:text-[17px] tw:md:leading-[1.55] tw:md:text-black',
            copy.text
        ),
        bar,
        stages
    );

    // Closing the popup stops a photo, so only the video can be left to finish in the background.
    if (kind === 'video') {
        // Try it on as a photo while the video is created.
        details.prepend(modeSwitch(session, 'tw:self-stretch tw:mb-[22px] tw:md:mb-[30px]'));
        const keepBrowsing = /** @type {HTMLButtonElement} */ (
            el(
                'button',
                'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:self-stretch tw:h-[41px] tw:mt-[26px] tw:px-3 tw:border tw:border-solid tw:border-black tw:bg-white tw:text-[12.5px] tw:font-medium tw:leading-normal tw:tracking-[0.54px] tw:text-black tw:cursor-pointer tw:md:self-start tw:md:h-[58px] tw:md:mt-9 tw:md:px-[26px] tw:md:border-[1.5px] tw:md:text-[15px] tw:md:tracking-[0.9px]',
                'KEEP BROWSING'
            )
        );
        keepBrowsing.type = 'button';
        keepBrowsing.addEventListener('click', closeModal);
        details.append(keepBrowsing);
    }

    /** @type {number | null} */
    let lastStage = null;
    /** @param {{ stage: number, percent: number }} progress */
    const update = ({stage, percent}) => {
        const rounded = Math.floor(percent);
        percentText.textContent = `${rounded}%`;
        fill.style.width = `${percent}%`;
        bar.setAttribute('aria-valuenow', String(rounded));
        if (stage === lastStage) return;
        lastStage = stage;

        stageText.textContent = waitStages[Math.min(stage, waitStages.length - 1)].label.toUpperCase();
        stageItems.forEach(({item, dot}, index) => {
            const done = index < stage;
            const now = index === stage;
            item.classList.toggle('tw:text-black', done || now);
            item.classList.toggle('tw:text-[#999]', !done && !now);
            dot.classList.toggle('tw:bg-[#1f7a3d]', done);
            dot.classList.toggle('tw:border-[#1f7a3d]', done);
            dot.classList.toggle('tw:bg-white', !done);
            dot.classList.toggle('tw:border-[#dd4124]', now);
            dot.classList.toggle('tw:border-[#ccc]', !done && !now);
            dot.replaceChildren(...(done ? [icon('vto-icon-check.svg', 'tw:!w-[11px] tw:!h-[11px] tw:md:!w-[14px] tw:md:!h-[14px]')] : []));
        });
    };

    const tick = () => update(waitProgress((Date.now() - startedAt) / 1000, kind));
    tick();
    const timer = setInterval(() => {
        if (details.isConnected) tick();
        else clearInterval(timer);
    }, 500);

    renderView(media, details, true);

    return {
        details,
        finish() {
            clearInterval(timer);
            update({stage: waitStages.length, percent: 100});
            // A moment at 100% before the result replaces it.
            return new Promise((resolve) => setTimeout(resolve, 600));
        },
    };
}

/**
 * Step 2: wait for the video.
 * @param {TryOnSession} session
 * @param {Promise<string>} promise
 */
async function showProgress(session, promise) {
    const {tryOnId} = session;
    const startedAt = videoWaitStarts.get(tryOnId) ?? getJob(tryOnId)?.startedAt ?? Date.now();
    videoWaitStarts.set(tryOnId, startedAt);
    const waiting = isActive(session) ? renderWaiting(session, 'video', startedAt) : null;

    /** @type {string} */
    let videoUrl;
    try {
        videoUrl = await promise;
    } catch (error) {
        videoWaitStarts.delete(tryOnId);
        // Only replace this view: the shopper may have switched to the photo meanwhile.
        if (!isActive(session) || !waiting?.details.isConnected) return;
        // The request is still stored when polling lost connection: offer to resume it rather than start a new one.
        if (getJob(tryOnId)) renderResumeError(session, error);
        else renderUpload(session, error);
        return;
    }

    videoWaitStarts.delete(tryOnId);
    if (!isActive(session) || !waiting?.details.isConnected) return;
    await waiting.finish();
    if (isActive(session) && waiting.details.isConnected) showResult(session, videoUrl);
}

/**
 * Polling gave up, but the generation may still be running on the server.
 * @param {TryOnSession} session
 * @param {unknown} error
 */
function renderResumeError(session, error) {
    const details = el('div', 'vto-details');
    const status = el('p', 'vto-status');
    status.setAttribute('role', 'status');
    details.append(status);
    showError(status, error);

    const retry = /** @type {HTMLButtonElement} */ (el('button', 'vto-add vto-add-all', 'Try again'));
    retry.type = 'button';
    retry.addEventListener('click', () => showTryOn(session));

    const restart = linkButton('Start again with new photos');
    restart.addEventListener('click', () => {
        clearJob(session.tryOnId);
        renderUpload(session);
    });

    details.prepend(el('h2', 'vto-title', 'Creating your video'));
    details.append(retry, restart);
    renderView(outfitMedia(session.imageUrl), details);
}

/** The result step's text for a video or a photo. */
const RESULT_COPY = {
    video: {
        ready: 'YOUR VIDEO IS READY',
        download: 'DOWNLOAD VIDEO',
        keep: 'Download it to keep it — guest videos are deleted after 30 days.',
    },
    photo: {
        ready: 'YOUR PHOTO IS READY',
        download: 'DOWNLOAD PHOTO',
        keep: 'We’ll keep it on this device for 30 days. Download it to keep it for good.',
    },
};

/**
 * Step 3: the video (or photo), then the look's products to add to the bag.
 * @param {TryOnSession} session
 * @param {string} url - the video's or the photo's URL
 * @param {'video' | 'photo'} [kind]
 */
async function showResult(session, url, kind = 'video') {
    const copy = RESULT_COPY[kind];
    // Desktop: the video stays put at the popup's height and only this column scrolls.
    const details = el(
        'div',
        'tw:flex tw:flex-col tw:px-4 tw:pt-[22px] tw:md:pt-[44px] tw:md:pb-12 tw:md:pl-14 tw:md:pr-16 tw:md:h-[min(900px,calc(100vh-64px))] tw:md:overflow-y-auto tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden tw:md:overscroll-contain'
    );

    const title = el(
        'p',
        'tw:mt-[6px] tw:mb-0 tw:text-[20px] tw:font-medium tw:leading-normal tw:tracking-[1px] tw:text-black tw:md:mt-[10px] tw:md:text-[34px] tw:md:font-normal tw:md:tracking-[1.4px]',
        'LOOKING SHARP.'
    );
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '2');

    const {shop, cta, status, load} = lookShop(session);

    const download = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:gap-[9px] tw:w-full tw:h-[41px] tw:mt-5 tw:px-3 tw:border tw:border-solid tw:border-black tw:bg-white tw:text-[12.5px] tw:font-medium tw:leading-normal tw:tracking-[0.54px] tw:text-black tw:cursor-pointer tw:disabled:opacity-50 tw:disabled:cursor-wait tw:md:gap-[10px] tw:md:h-14 tw:md:mt-3 tw:md:border-[1.5px] tw:md:text-[15px] tw:md:tracking-[0.9px]'
        )
    );
    download.type = 'button';
    download.append(
        icon('vto-icon-download.svg', 'tw:!w-4 tw:!h-4 tw:md:!w-[18px] tw:md:!h-[18px]'),
        el('span', '', copy.download)
    );
    download.addEventListener('click', () => downloadFile(url, download, kind === 'video' ? 'mp4' : 'jpg'));

    // Back to the first step to make a new video or photo. This one is kept until the new one is
    // ready, so the first step links back to it.
    const retry = /** @type {HTMLButtonElement} */ (
        el(
            'button',
            'tw:self-start tw:mt-[10px] tw:p-0 tw:border-0 tw:bg-transparent tw:text-left tw:text-[13px] tw:leading-normal tw:text-black tw:underline tw:cursor-pointer tw:md:mt-2 tw:md:text-[16px]',
            'Try again'
        )
    );
    retry.type = 'button';
    retry.addEventListener('click', () => renderUpload(session));

    session.mode = kind;
    details.append(
        modeSwitch(session, 'tw:mb-[22px] tw:md:mb-[30px]'),
        el(
            'p',
            'tw:m-0 tw:text-[12px] tw:font-bold tw:leading-normal tw:tracking-[1.4px] tw:text-[#1f7a3d] tw:md:text-[13px] tw:md:tracking-[1.8px]',
            copy.ready
        ),
        title,
        el(
            'p',
            'tw:mt-2 tw:mb-0 tw:text-[13px] tw:leading-[1.4] tw:text-[#333] tw:md:mt-[10px] tw:md:text-[17px] tw:md:leading-[1.55] tw:md:text-black',
            'Told you. Now let’s get the real thing to your door. — Max'
        ),
        shop,
        cta,
        status,
        download,
        el(
            'p',
            'tw:mt-3 tw:mb-0 tw:text-[12px] tw:leading-[1.4] tw:text-[#454545] tw:md:mt-[14px] tw:md:text-[15px] tw:md:leading-normal tw:md:text-[#444]',
            copy.keep
        ),
        retry
    );

    const media = el('div');
    media.append(mobileTopBar(), kind === 'video' ? videoMedia(url) : photoMedia(url));
    renderView(media, details, true);
    // The shopper has seen the video: no need for the notifier's toast.
    if (kind === 'video') updateJob(session.tryOnId, {notified: true});
    await load();
}

/**
 * The look's products with their "Add the complete look" button and its status, for the result
 * and for "Just shop the look". `load` fills them in, swapping only the products so a video keeps
 * playing.
 * @param {TryOnSession} session
 */
function lookShop(session) {
    // The products load in the background (usually already warm from opening the popup).
    const shop = el('div', 'tw:mt-[22px] tw:md:mt-[26px]');
    shop.append(el('p', 'tw:m-0 tw:text-[15px] tw:leading-normal tw:text-[#444]', 'Loading the look…'));

    // "Add the complete look". Mobile: last, and stays at the bottom of the screen while the popup
    // scrolls. Desktop: right under the products.
    const cta = el(
        'div',
        'tw:order-last tw:sticky tw:bottom-0 tw:z-[2] tw:flex tw:flex-col tw:gap-2 tw:-mx-4 tw:mt-6 tw:px-4 tw:pt-3 tw:pb-4 tw:bg-white tw:empty:hidden tw:md:order-none tw:md:static tw:md:block tw:md:mx-0 tw:md:mt-0 tw:md:p-0'
    );

    const status = el('p', 'tw:mt-3 tw:mb-0 tw:text-[14px] tw:leading-normal tw:text-black tw:whitespace-pre-line tw:empty:hidden tw:[&.is-error]:text-[#dd4124]');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    async function load() {
        /** @type {HTMLElement} */
        let content;
        try {
            const {look, items} = await loadLook(session.tryOnId);
            content = renderLook(look, items, status, cta);
        } catch (error) {
            content = el(
                'p',
                'tw:m-0 tw:text-[15px] tw:leading-normal tw:text-[#444]',
                /** @type {Error} */ (error).message === 'not-found'
                    ? 'Sorry, this look could not be found.'
                    : 'Something went wrong loading this look. Please try again.'
            );
        }
        if (isActive(session) && shop.isConnected) shop.replaceChildren(content);
    }

    return {shop, cta, status, load};
}

/**
 * "Just shop the look": the outfit and its products, straight to the bag without a video or photo.
 * @param {TryOnSession} session
 */
async function renderShopLook(session) {
    const details = el(
        'div',
        'tw:flex tw:flex-col tw:px-4 tw:pt-[22px] tw:md:pt-[44px] tw:md:pb-12 tw:md:pl-14 tw:md:pr-16 tw:md:h-[min(900px,calc(100vh-64px))] tw:md:overflow-y-auto tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden tw:md:overscroll-contain'
    );

    const title = el(
        'p',
        'tw:mt-[6px] tw:mb-0 tw:text-[20px] tw:font-medium tw:leading-normal tw:tracking-[1px] tw:text-black tw:md:mt-[10px] tw:md:text-[34px] tw:md:font-normal tw:md:tracking-[1.4px]',
        'THE COMPLETE LOOK'
    );
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '2');

    const {shop, cta, status, load} = lookShop(session);

    // Back to the first step, keeping any photos already picked.
    const back = linkButton('← Try it on first');
    back.classList.add('tw:self-start', 'tw:!mt-0', 'tw:mb-[22px]', 'tw:md:mb-[30px]');
    back.addEventListener('click', () => renderUpload(session));

    details.append(
        back,
        el(
            'p',
            'tw:m-0 tw:text-[12px] tw:font-bold tw:leading-normal tw:tracking-[1.4px] tw:text-[#dd4124] tw:md:text-[13px] tw:md:tracking-[1.8px]',
            'JUST SHOP THE LOOK'
        ),
        title,
        el(
            'p',
            'tw:mt-2 tw:mb-0 tw:text-[13px] tw:leading-[1.4] tw:text-[#333] tw:md:mt-[10px] tw:md:text-[17px] tw:md:leading-[1.55] tw:md:text-black',
            'Every piece, just as it’s styled. Pick your sizes and it’s on its way. — Max'
        ),
        shop,
        cta,
        status
    );

    // The outfit: under the top bar on mobile, the popup's height on desktop.
    const media = el('div');
    const frame = el('div', 'tw:bg-[#f2f2f2] tw:md:h-[min(900px,calc(100vh-64px))]');
    const image = /** @type {HTMLImageElement} */ (
        el('img', 'tw:block tw:!w-full tw:!h-[min(60vh,480px)] tw:object-cover tw:md:!h-full')
    );
    setOutfitImage(image, session.imageUrl);
    image.alt = 'The look';
    frame.append(image);
    media.append(mobileTopBar(), frame);

    renderView(media, details, true);
    await load();
}

/* ----- Try-on photo ----- */

/**
 * The try-on photo (9:16, about 1536x2752) in a frame of the same shape.
 * @param {string} imageUrl
 */
function photoMedia(imageUrl) {
    const media = el(
        'div',
        'tw:flex tw:items-center tw:justify-center tw:overflow-hidden tw:px-4 tw:pt-2 tw:pb-4 tw:bg-[#f2f2f2] tw:md:h-[min(900px,calc(100vh-64px))] tw:md:p-0'
    );
    // Mobile: full width, but no taller than 70% of the screen. Desktop: the popup's height.
    const frame = el(
        'div',
        'tw:relative tw:w-full tw:max-w-[calc(70vh*9/16)] tw:aspect-[9/16] tw:md:w-auto tw:md:max-w-full tw:md:h-full'
    );

    const image = /** @type {HTMLImageElement} */ (
        el('img', 'tw:absolute tw:inset-0 tw:!w-full tw:!h-full tw:object-contain')
    );
    image.src = imageUrl;
    image.alt = 'You, wearing this look';
    frame.append(image);

    media.append(frame);
    return media;
}

/**
 * Stops creating the session's try-on photo (the popup closed, or the shopper started over).
 * @param {TryOnSession} session
 */
function stopPhoto(session) {
    session.photoRun?.abort();
    session.photoRun = null;
}

/**
 * Uploads the photos and waits for the try-on photo on the waiting screen.
 * @param {TryOnSession} session
 * @param {File[]} photos
 */
async function startPhoto(session, photos) {
    stopPhoto(session);
    const controller = new AbortController();
    session.photoRun = controller;

    const waiting = renderWaiting(session, 'photo', Date.now());

    try {
        const imageUrl = await createTryOnPhoto(session.tryOnId, photos, {
            signal: controller.signal,
            // The waiting screen follows its own timeline.
            onProgress: () => {
            },
        });
        if (controller.signal.aborted) return;
        // Kept in the background: the fal.ai link works for now.
        session.photoUrl = imageUrl;
        storePhoto(session.tryOnId, imageUrl, {productUrl: location.pathname, outfitImageUrl: session.imageUrl});
        if (!isActive(session)) return;
        await waiting.finish();
        if (isActive(session) && !controller.signal.aborted) showResult(session, imageUrl, 'photo');
    } catch (error) {
        if (!controller.signal.aborted && isActive(session)) renderPhotoError(session, photos, error);
    } finally {
        if (session.photoRun === controller) session.photoRun = null;
    }
}

/**
 * The photo couldn't be created: try again with the same photos, or pick new ones.
 * @param {TryOnSession} session
 * @param {File[]} photos
 * @param {unknown} error
 */
function renderPhotoError(session, photos, error) {
    const details = el('div', 'vto-details');
    const status = el('p', 'vto-status');
    status.setAttribute('role', 'status');
    showError(status, error);

    const retry = /** @type {HTMLButtonElement} */ (el('button', 'vto-add vto-add-all', 'Try again'));
    retry.type = 'button';
    retry.addEventListener('click', () => {
        retry.disabled = true;
        startPhoto(session, photos);
    });

    const restart = linkButton('Choose different photos');
    restart.addEventListener('click', () => renderUpload(session));

    details.append(el('h2', 'vto-title', 'Creating your photo'), status, retry, restart);
    renderView(outfitMedia(session.imageUrl), details);
}

/**
 * The outfit's finished video or photo, if it's kept.
 * @param {TryOnSession} session
 * @param {'video' | 'photo'} kind
 */
function getResult(session, kind) {
    if (kind === 'photo') return session.photoUrl;
    // A video on the device (finished or still being made) is newer than the one saved to the account.
    const job = getJob(session.tryOnId);
    if (job || getRun(session.tryOnId)) return job?.videoUrl;
    return session.videoUrl;
}

/**
 * Shows the outfit's video or photo: the result if it's ready, the video's progress if it's being
 * created (or stored from before a reload), otherwise the upload form for it.
 * @param {TryOnSession} session
 * @param {'video' | 'photo'} mode
 */
function showMode(session, mode) {
    const {tryOnId} = session;
    session.mode = mode;
    // While the photo is shown, the notifier's toast tells the shopper when the video is ready.
    setViewing(mode === 'video' ? tryOnId : null);

    const result = getResult(session, mode);
    const job = mode === 'video' ? getJob(tryOnId) : null;
    const running = mode === 'video' ? getRun(tryOnId) : null;

    if (result) showResult(session, result, mode);
    else if (running) showProgress(session, running);
    else if (job) showProgress(session, resumeJob(tryOnId, job));
    else renderUpload(session);
}

/**
 * Shows the right step for the outfit: the video's if one is ready or being created, otherwise
 * the photo if one is kept, otherwise the upload form.
 * @param {TryOnSession} session
 */
function showTryOn(session) {
    const hasVideo = !!getJob(session.tryOnId) || !!getRun(session.tryOnId) || !!session.videoUrl;
    showMode(session, hasVideo || !session.photoUrl ? 'video' : 'photo');
}

/**
 * @param {{ tryOnId?: string, imageUrl?: string }} options
 */
function virtualTryOn({tryOnId, imageUrl} = {}) {
    // Already open on this outfit's photo (the "video is ready" toast): just switch to the video.
    if (current && current.tryOnId === tryOnId && isActive(current)) {
        showMode(current, 'video');
        return;
    }

    if (current) stopPhoto(current);
    openModal();

    if (!tryOnId) {
        renderMessage('This look is not available.');
        return;
    }

    /** @type {TryOnSession} */
    const session = {
        tryOnId,
        imageUrl: imageUrl || getJob(tryOnId)?.imageUrl,
        photos: Array(PHOTO_COUNT).fill(null),
        previews: new Map(),
        consent: false,
        shareConsent: false,
        mode: 'video',
        photoRun: null,
        photoUrl: null,
        videoUrl: null,
    };
    current = session;
    setViewing(tryOnId);
    // Warm the look's products so they're ready when the video is.
    loadLook(tryOnId).catch(() => {
    });

    // The kept photo and the shopper's saved photos are read from IndexedDB first (quick), so the right
    // step shows straight away, with the page's image (or a plain background) until the look's own image
    // arrives. Without a video job on the device, the account's saved video and photo are asked for too.
    renderMessage('');
    let notFound = false;
    const fromDevice = !!getJob(tryOnId) || !!getRun(tryOnId);
    Promise.all([
        getStoredPhoto(tryOnId),
        getSavedPhotos(),
        fromDevice ? null : getAccountMediaFor(tryOnId),
    ]).then(([photoUrl, saved, account]) => {
        session.photoUrl = photoUrl || account?.photo?.url || null;
        session.videoUrl = account?.video?.url || null;
        saved.forEach(({file}, index) => {
            if (index < PHOTO_COUNT && !session.photos[index]) session.photos[index] = file;
        });
        if (isActive(session) && !notFound) showTryOn(session);
    });

    // The outfit's image comes from the look (`outfitImageUrls[0]`). If it has none or can't be
    // loaded, the page's image stays. A look that no longer exists isn't retried.
    getOutfitImageUrl(tryOnId).then(
        (url) => {
            if (!url) return;
            session.imageUrl = url;
            if (isActive(session)) updateOutfitImages(url);
        },
        (error) => {
            if (error.message !== 'not-found') return;
            notFound = true;
            if (isActive(session)) {
                renderMessage('This outfit is no longer available.');
            }
        }
    );
}

/**
 * Loads a look's image ahead of a click, so the popup opens with it.
 * @param {string} tryOnId
 */
function preloadOutfitImage(tryOnId) {
    getOutfitImageUrl(tryOnId)
        .then((url) => {
            if (url) new Image().src = url;
        })
        .catch(() => {
        });
}

// Buttons without a page image (Outfit of the Day) get the look's image ahead of a click.
document
    .querySelectorAll('.virtual-try-on-button[data-try-on-id]:not([data-image-url])')
    .forEach((button) => preloadOutfitImage(/** @type {HTMLElement} */ (button).dataset.tryOnId || ''));

// @ts-ignore - exposed for the gallery button handler and the notifier's toast
window.virtualTryOn = virtualTryOn;

// `?vto={outfitId}` (from the notifier's toast on another page) opens the popup for that outfit.
{
    const url = new URL(location.href);
    const tryOnId = url.searchParams.get('vto');
    if (tryOnId) {
        url.searchParams.delete('vto');
        history.replaceState(history.state, '', url);
        const button = /** @type {HTMLElement | null} */ (
            document.querySelector(`.virtual-try-on-button[data-try-on-id="${CSS.escape(tryOnId)}"]`)
        );
        virtualTryOn({tryOnId, imageUrl: button?.dataset.imageUrl});
    }
}
